import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import translations from './practiceplaceprofile.i18n';
import * as css from './practiceplaceprofile.module.scss';

import PageLayout from '../../pagelayout';
import { BackButton } from '../../pagelayout/backButton';
import classnames from 'classnames';
import Button from '../../components/button';
import PanelHeader from '../../components/panelheader/panelHeader';
import Icon, { IconType } from '../../components/icon';
import { formatUrlNoScheme } from '../utils';

interface Praksissted {
  id: string;
  navn: string;
}

interface Adresse {
  adresselinje1?: string;
  adresselinje2?: string;
  postnr?: string;
  poststed?: string;
}

interface Bruker {
  fornavn: string;
  etternavn: string;
}

interface Praksisstedprofil {
  id: string;
  navn: string;
  orgnr?: string;
  avdeling?: Praksissted;
  adresse?: Adresse;
  nettsideUrl?: string;
  epost?: string;
  telefonnummer?: string;
  bildeUrl?: string;
  beskrivelse?: string;
  krav?: string;
  sistEndretAvPraksisstedbruker?: Bruker;
  sistEndretAvStudiestedbruker?: Bruker;
  sistEndret?: string;
}

interface Props {
  data: Praksisstedprofil;
  menuItems: React.ReactNode;
  canEdit: boolean;
  saveHandler: (beskrivelse: string, krav: string) => void;
}

export const PracticePlaceProfileStudent: React.FC<
  React.PropsWithChildren<Props>
> = ({ data, menuItems, canEdit, saveHandler }) => {
  const { formatMessage } = useIntl();

  const cancelEdit = () => {
    setIsEditMode(false);
  };

  const saveEdit = () => {
    saveHandler(beskrivelse, krav);
    setIsEditMode(false);
  };

  const labels = {
    title: formatMessage(
      { id: translations.title.id },
      { practicePlaceName: data.navn },
    ),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    tilbake: formatMessage({ id: translations.tilbakeStudent.id }),
    endre: formatMessage({ id: translations.endre.id }),
    avbryt: formatMessage({ id: translations.avbryt.id }),
    lagre: formatMessage({ id: translations.lagre.id }),
    profileImgAltText: formatMessage({ id: translations.profileImgAltText.id }),
    eier: formatMessage({ id: translations.eier.id }),
    orgnr: formatMessage({ id: translations.orgnr.id }),
    kontaktinfo: formatMessage({ id: translations.kontaktinfo.id }),
    epost: formatMessage({ id: translations.epost.id }),
    telefon: formatMessage({ id: translations.telefon.id }),
    nettside: formatMessage({ id: translations.nettside.id }),
    beskrivelse: formatMessage({ id: translations.beskrivelse.id }),
    krav: formatMessage({ id: translations.krav.id }),
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [beskrivelse, setBeskrivelse] = useState(data.beskrivelse || '');
  const [krav, setKrav] = useState(data.krav || '');

  const actionItems = (
    <>
      <BackButton title={labels.tilbake} path={'/'} />
      {!isEditMode && canEdit && (
        <Button.Primary onClick={() => setIsEditMode(true)}>
          {labels.endre}
        </Button.Primary>
      )}
      {isEditMode && (
        <>
          <Button.Primary onClick={cancelEdit}>{labels.avbryt}</Button.Primary>
          <Button.Primary onClick={saveEdit} disabled={false}>
            {labels.lagre}
          </Button.Primary>
        </>
      )}
    </>
  );

  const endretAv = () => {
    if (data.sistEndretAvPraksisstedbruker) {
      return (
        data.sistEndretAvPraksisstedbruker.fornavn +
        ' ' +
        data.sistEndretAvPraksisstedbruker.etternavn
      );
    } else if (data.sistEndretAvStudiestedbruker) {
      return (
        data.sistEndretAvStudiestedbruker.fornavn +
        ' ' +
        data.sistEndretAvStudiestedbruker.etternavn
      );
    }
    return null;
  };

  return (
    <PageLayout
      isIndented={true}
      title={labels.title}
      subtitle={labels.subtitle}
      menuItems={menuItems}
      actionItems={actionItems}
    >
      <Grid container className={css.container} alignItems={'stretch'}>
        <Grid item xs={12} sm={12} md={8} lg={7} xl={6}>
          <div
            className={classnames(css.pictureContainer, 'pictureContainer')}
            aria-hidden="true"
          >
            {data.bildeUrl && (
              <img
                src={data.bildeUrl}
                className={css.profilePicture}
                alt={labels.profileImgAltText}
              />
            )}
            {!data.bildeUrl && (
              <div className={css.profilePictureReplacement}>
                <div className={css.iconWrapper}>
                  <Icon type={IconType.EndNode} title={''} />
                </div>
              </div>
            )}
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={5} xl={6} className={css.info}>
          <div
            className={classnames(
              css.label,
              css.practicePlaceName,
              'practiceplacename',
            )}
          >
            {data.navn}
          </div>
          {data.orgnr && (
            <div className={css.orgnr}>
              <span className={css.label}>{labels.orgnr}:</span>{' '}
              <span className="organizationNumber">{data.orgnr}</span>
            </div>
          )}
          {data.avdeling && (
            <div>
              <span className={css.label}>{labels.eier}:</span>{' '}
              <span className="departmentName">{data.avdeling.navn}</span>
            </div>
          )}
          <PanelHeader title={labels.kontaktinfo} backgroundColor={'#ffffff'} />
          {data.adresse && (
            <>
              {data.adresse.adresselinje1 && (
                <div className="streetAddress">
                  {data.adresse.adresselinje1}
                </div>
              )}
              {data.adresse.adresselinje2 && (
                <div className="streetAddress">
                  {data.adresse.adresselinje2}
                </div>
              )}
              <div className="postalAddress">
                {data.adresse.postnr} {data.adresse.poststed}
              </div>
            </>
          )}
          {data.nettsideUrl && (
            <div>
              <span className={css.label}>{labels.nettside}:</span>{' '}
              <a href={data.nettsideUrl} className="websiteUrl">
                {formatUrlNoScheme(data.nettsideUrl)}
              </a>
            </div>
          )}
          {data.epost && (
            <div>
              <span className={css.label}>{labels.epost}:</span>{' '}
              <a href={`mailto: ${data.epost}`} className="email">
                {data.epost}
              </a>
            </div>
          )}
          {data.telefonnummer && (
            <div>
              <span className={css.label}>{labels.telefon}:</span>{' '}
              <span className="phone">{data.telefonnummer}</span>
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container={true} className={css.container}>
        {(data.beskrivelse || isEditMode) && (
          <Grid item={true} xs={12} sm={7}>
            <PanelHeader title={labels.beskrivelse} />
            {isEditMode ? (
              <textarea
                value={beskrivelse}
                onChange={(e) => setBeskrivelse(e.target.value)}
              />
            ) : (
              <p>{data.beskrivelse}</p>
            )}
          </Grid>
        )}
        {(data.krav || isEditMode) && (
          <Grid item={true} xs={12} sm={7}>
            <PanelHeader title={labels.krav} />
            {isEditMode ? (
              <textarea
                value={krav}
                onChange={(e) => setKrav(e.target.value)}
              />
            ) : (
              <p>{data.krav}</p>
            )}
          </Grid>
        )}
        {endretAv() && (
          <>
            <Grid item={true} xs={7}>
              <p style={{ marginTop: '2em' }}>
                Sist redigert av {endretAv()}, {data.sistEndret}
              </p>
            </Grid>
            <Grid item={true} xs={5} />
          </>
        )}
      </Grid>
    </PageLayout>
  );
};
